// src/pages/auth/SignUpPage.js
/* Filepath: // src/pages/auth/SignUpPage.js */
import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Container,
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  signUpUser,
  getFriendlyErrorMessage,
} from "../../services/authService";
import useAuthStore from "../../store/useAuthStore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BrandLogo from "../../components/BrandLogo";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";

const PAIR_QS_KEY = "__PAIR_QS__";

export default function SignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchQS = location.search || "";
  const { setUser } = useAuthStore();

  // Persist current QS (if it contains a code) so we can recover after sign-up
  useEffect(() => {
    const hasCode = new URLSearchParams(searchQS).has("code");
    if (hasCode) {
      try {
        sessionStorage.setItem(PAIR_QS_KEY, searchQS);
      } catch {}
    }
  }, [searchQS]);

  const storedPairQS = (() => {
    try {
      return sessionStorage.getItem(PAIR_QS_KEY) || "";
    } catch {
      return "";
    }
  })();

  // Auth state (to hide the form if already signed in)
  const [authedUser, setAuthedUser] = useState(null);
  useEffect(() => {
    const auth = getAuth();
    setAuthedUser(auth.currentUser || null);
    const unsub = onAuthStateChanged(auth, (u) => setAuthedUser(u || null));
    return unsub;
  }, []);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Flow state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Validation helpers
  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const isValidPassword = (val) =>
    /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{6,})/.test(val);

  // Guard to avoid double navigate when *we* just completed sign-up here
  const authActionInProgressRef = useRef(false);

  // Only redirect AFTER a sign-in that happens while this page is mounted.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!authedUser) return;
    if (authActionInProgressRef.current) return;

    const currentHasCode = new URLSearchParams(searchQS).has("code");
    const storedHasCode = new URLSearchParams(storedPairQS || "").has("code");
    const finalQS = currentHasCode ? searchQS : storedHasCode ? storedPairQS : "";

    if (finalQS && new URLSearchParams(finalQS).has("code")) {
      navigate(`/pair${finalQS}`, { replace: true });
    } else {
      navigate("/interact", { replace: true });
    }
  }, [authedUser, searchQS, storedPairQS, navigate]);

  const handleSignUp = async () => {
    setLoading(true);
    setError("");
    authActionInProgressRef.current = true;

    try {
      const user = await signUpUser(email, password);
      setUser(user);

      // Pick the best available QS for pairing redirect
      const currentHasCode = new URLSearchParams(searchQS).has("code");
      const storedHasCode = new URLSearchParams(storedPairQS || "").has("code");
      const finalQS = currentHasCode ? searchQS : storedHasCode ? storedPairQS : "";

      if (finalQS && new URLSearchParams(finalQS).has("code")) {
        try { sessionStorage.setItem(PAIR_QS_KEY, finalQS); } catch {}
        navigate(`/pair${finalQS}`, {
          replace: true,
          state: { cameFromAuth: true },
        });
      } else {
        navigate("/interact", { replace: true });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error signing up:", err?.message || err);
      setError(getFriendlyErrorMessage(err?.code));
      setSnackbarOpen(true);
    } finally {
      authActionInProgressRef.current = false;
      setLoading(false);
    }
  };

  // Safe sign-out (then go to /signin, preserving pairing QS if available)
  const handleSignInAsDifferent = async (e) => {
    e.preventDefault();
    if (switching) return;
    setSwitching(true);
    try {
      const auth = getAuth();
      try {
        await signOut(auth);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Sign out failed (continuing):", err);
      }
      try { localStorage.clear(); } catch {}
      try { sessionStorage.setItem(PAIR_QS_KEY, searchQS || storedPairQS || ""); } catch {}
      try {
        if (indexedDB?.databases) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs.map(
              (db) =>
                db?.name &&
                new Promise((res) => {
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => res();
                })
            )
          );
        } else {
          ["firebaseLocalStorageDb", "firebase-installations-database"].forEach((name) => {
            try { indexedDB.deleteDatabase(name); } catch {}
          });
        }
      } catch {}

      const qsForNav =
        searchQS && new URLSearchParams(searchQS).toString()
          ? searchQS
          : storedPairQS || "";
      navigate(`/signin${qsForNav}`, { replace: true });
    } finally {
      setSwitching(false);
    }
  };

  return (
    <>
      <Backdrop sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing up...
          </Typography>
        </Box>
      </Backdrop>

      <Container
        maxWidth="xs"
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: 4,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <BrandLogo size={100} mb={8} />

          {/* If they're already logged in, don't show a form */}
          {authedUser ? (
            <Typography align="center">
              You’re signed in with {authedUser.email || "your account"}.{" "}
              <Link
                underline="hover"
                onClick={handleSignInAsDifferent}
                sx={{ cursor: switching ? "not-allowed" : "pointer", opacity: switching ? 0.7 : 1 }}
              >
                {switching ? "Signing out…" : "Sign in as a different user"}
              </Link>
            </Typography>
          ) : (
            <>
              <Typography variant="h4" sx={{ mb: 2 }} align="center">
                Sign Up with Email Address
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    variant="outlined"
                    autoComplete="email"
                  />
                  {email && !isValidEmail(email) && (
                    <Typography color="error" fontSize="0.8rem" mt={0.5}>
                      Please enter a valid email address.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    placeholder="Create Password"
                    value={password}
                    onChange={(e) => setPassword({ target: { value: e.target.value.trimStart() } }.target.value)}
                    fullWidth
                    variant="outlined"
                    autoComplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" sx={{ pr: 2 }}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {password && !isValidPassword(password) && (
                    <Typography color="error" fontSize="0.8rem" mt={0.5}>
                      Password must be at least 6 characters, include 1 uppercase
                      letter and 1 special character.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Button
                    onClick={handleSignUp}
                    variant="contained"
                    fullWidth
                    disabled={!isValidEmail(email) || !isValidPassword(password) || loading}
                  >
                    Sign Up
                  </Button>

                  <Box sx={{ mt: 3 }} />

                  <Typography align="center">
                    Already a member?{" "}
                    <Link
                      onClick={() => navigate(`/signin${(searchQS && new URLSearchParams(searchQS).has("code")) ? searchQS : (storedPairQS || "")}`)}
                      underline="hover"
                    >
                      Sign in!
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
